/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * My Devices › an SG: Open SG. Opens the full SG app in a new tab, from
 * anywhere, carried to the boat over the SG's own connection to Vortex
 * (nothing on the boat is opened to the internet). Shows whether the SG is
 * connected right now. The first and biggest thing on an SG's page.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';

export function OpenSgButton({ deviceId }: { deviceId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['gateway-remote-status', deviceId],
    queryFn: () => api.getGatewayRemoteStatus(deviceId),
    refetchInterval: 30_000,
    retry: false,
  });
  const online = data?.online === true;

  return (
    <div className={`rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border ${
      online ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-slate-700/50 border-slate-600'
    }`} data-testid="open-sg">
      <div className="min-w-0 flex items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${online ? 'bg-green-400' : 'bg-slate-500'}`} aria-hidden />
        <p className={`text-sm ${online ? 'text-green-400' : 'text-slate-300'}`}>
          {isLoading ? 'Checking…' : online ? 'Connected: open your SG from anywhere' : 'Not connected right now (SG is off, or has no internet)'}
        </p>
      </div>
      <a
        href={api.gatewayRemoteAppUrl(deviceId)}
        target="_blank"
        rel="noopener"
        aria-disabled={!online}
        onClick={(e) => { if (!online) e.preventDefault(); }}
        className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base font-semibold shrink-0 ${
          online ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-slate-600/50 text-slate-400 cursor-not-allowed'
        }`}
      >
        Open SG
        <ArrowTopRightOnSquareIcon className="w-5 h-5" />
      </a>
    </div>
  );
}
